import {
  Badge,
  FieldWrapper,
  FlexBox,
  Icon,
  InputSelectionList,
  InputSelectionListChangeEvent,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { Schedule } from 'lib-shared-types-api';
import { memo, useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { flowsApi } from 'apis/flows';
import { AppLink } from 'components/appLink';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { articles } from 'utils/elevio';

import { AnalyticsDocumentationButton } from '../../../../../documentation/analyticsDocumentationButton';
import { AnalyticsFilterItem, AnalyticsFilterLayout } from '../../../filter';

import styles from './analyticsBusinessHours.module.scss';

type AnalyticsBusinessHoursProps = {
  /** The id of the selected schedule. */
  scheduleId?: string;
  /** Whether a period has been selected. If not, disable the input. */
  isPeriodSelected: boolean;
  /** Callback for when the schedule is changed. */
  onChangeSchedule: (schedule?: Schedule) => void;
};

export const AnalyticsBusinessHours = memo(function AnalyticsBusinessHours({
  scheduleId,
  isPeriodSelected,
  onChangeSchedule,
}: AnalyticsBusinessHoursProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScheduleId, setSelectedScheduleId] = useState<
    string | undefined
  >(scheduleId);

  const {
    arg,
    asyncStatus,
    items: schedules,
    changeArg,
    hasNextPage,
    nextPage,
  } = usePaginatedEndpoint({
    endpoint: flowsApi.endpoints.readFlowsSchedules,
    initialArg: {
      limit: 50,
    },
    itemsSelector: ({ data }) => data.schedules,
    infinite: true,
  });

  // The currently selected schedule. The query refetches when `selectedScheduleId` changes, and
  // if previously cached, the API request is skipped and the cached result is returned. The query
  // is skipped initially if no schedule is selected.
  const { data: selectedSchedule, isFetching } =
    flowsApi.useReadFlowScheduleQuery(
      selectedScheduleId
        ? {
            id: selectedScheduleId,
          }
        : skipToken,
    );

  // The selected schedule is fetched and updated when `selectedScheduleId` changes. Once
  // fulfilled, the full selected schedule object is passed to the parent component.
  useUpdateEffect(() => {
    if (!selectedScheduleId) {
      onChangeSchedule(undefined);
    } else if (selectedScheduleId === selectedSchedule?.id) {
      onChangeSchedule(selectedSchedule);
    }
  }, [selectedSchedule, selectedScheduleId]);

  useUpdateEffect(() => {
    changeArg({ searchTerm, offset: 0 }, { debounceTime: 300 });
  }, [changeArg, searchTerm]);

  const handleToggleDropdown = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, []);

  /**
   * Handles selecting and de-selecting the schedule id.
   *
   * When selecting a schedule, an API request is triggered to fetch the full
   * schedule object to be passed to the parent component.
   */
  const handleChangeSchedule = useCallback(
    (event: InputSelectionListChangeEvent<string>) => {
      const [scheduleId] = event.selectedValues;
      setSelectedScheduleId(scheduleId);
    },
    [],
  );

  /** Handles rendering of the selected schedule in the input field. */
  const renderSelectedSchedule = useCallback(() => {
    if (!selectedSchedule || isFetching) return 'Loading...';

    return (
      <div className={styles.selectedSchedule}>
        <Text.Paragraph>{selectedSchedule.name}</Text.Paragraph>

        <Tooltip content="Open schedule">
          <AppLink
            path="/settings/businesshours/:id"
            params={{
              path: { id: selectedSchedule.id },
            }}
            target="_blank"
          >
            <Icon name="external-link" />
          </AppLink>
        </Tooltip>

        <Badge variant="secondary" size="small">
          {selectedSchedule.timezone}
        </Badge>
      </div>
    );
  }, [isFetching, selectedSchedule]);

  const renderVersionNotice = useCallback(() => {
    if (
      !selectedScheduleId ||
      !selectedSchedule ||
      selectedSchedule.version === 1
    ) {
      return null;
    }

    return (
      <Tooltip
        content={
          <Text.Paragraph color="inherit">
            Analytics will reflect the business hours that were in place{' '}
            <b>during the period you are looking at</b>.
          </Text.Paragraph>
        }
        className={styles.tooltipNotice}
        wrapProps={{ className: styles.tooltipNoticeWrapper }}
      >
        <Icon name="exclamation" size={16} color={colors.blue[400]} />
      </Tooltip>
    );
  }, [selectedSchedule, selectedScheduleId]);

  const renderNewBadge = useCallback(() => {
    // Stop rendering the new badge after September 30, 2023.
    if (new Date() > new Date(2023, 8, 30)) return null;

    return (
      <Badge variant="primary" size="small">
        New
      </Badge>
    );
  }, []);

  return (
    <FieldWrapper
      label={
        <FlexBox gap="200" alignItems="center">
          {renderNewBadge()}

          <span>Business hours</span>

          <AnalyticsDocumentationButton
            type="article"
            articleId={articles.analytics.businessHours}
          />

          {renderVersionNotice()}
        </FlexBox>
      }
      labelFor="none"
      noAlert
    >
      <Tooltip content="Please select a date first" disabled={isPeriodSelected}>
        <InputSelectionList<string>
          value={selectedScheduleId ? [selectedScheduleId] : []}
          placeholder="Select a schedule"
          clearable
          disabled={!isPeriodSelected}
          onChange={handleChangeSchedule}
          onToggleDropdown={handleToggleDropdown}
          filter={{
            value: searchTerm,
            onChange: (e) => setSearchTerm(e.target.value),
          }}
          getItemTitleByValue={renderSelectedSchedule}
          dropdownProps={{ className: styles.dropdown }}
          data-testid={analyticsBusinessHoursTestId}
        >
          {schedules.map((schedule) => (
            <AnalyticsFilterItem
              key={schedule.id}
              label={schedule.name}
              value={schedule.id}
              highlight={arg?.searchTerm}
              contentClassName={styles.itemContent}
              noFixedSelectionStateSpace
            >
              <Badge variant="secondary" size="small">
                {schedule.timezone}
              </Badge>
            </AnalyticsFilterItem>
          ))}

          {asyncStatus === 'pending' && <AnalyticsFilterLayout.Loading />}

          {asyncStatus === 'fulfilled' && !schedules.length && (
            <AnalyticsFilterLayout.Empty />
          )}

          {asyncStatus === 'fulfilled' && hasNextPage && (
            <Waypoint onEnter={nextPage} />
          )}
        </InputSelectionList>
      </Tooltip>
    </FieldWrapper>
  );
});

export const analyticsBusinessHoursTestId = 'analytics-business-hours';
